import React from 'react';
import { motion } from 'framer-motion';
import { Send, ChevronDown } from 'lucide-react';
import { AnimatedCounter } from '@/components/common/AnimatedCounter';

const VIDEO_URL = '/hero-bg.mp4';
const VIDEO_URL_MOBILE = '/hero-bg-mobile.mp4';
const POSTER = '/hero-poster.jpg';

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const HeroStats = ({ compact }) => (
  compact ? (
    <div className="flex items-start justify-between gap-1 px-1" data-testid="hero-stats">
      {[
        [<AnimatedCounter key="a" prefix="+" value={7000} />, 'Serviços', 'Realizados'],
        [<AnimatedCounter key="b" isInfinity />, 'Incontáveis', 'Participantes'],
        [<AnimatedCounter key="c" value={99.9} suffix="%" />, 'Aprovação', 'do Público'],
        [<AnimatedCounter key="d" isInfinity />, 'Clientes', 'Satisfeitos'],
      ].map(([num, l1, l2], i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="w-px self-stretch bg-white/10 my-1" />}
          <div className="flex-1 text-center">
            <p className="font-heading text-xl text-[#00A8E8] leading-none">{num}</p>
            <p className="text-white/55 text-[10px] font-body leading-tight mt-1.5">{l1}<br />{l2}</p>
          </div>
        </React.Fragment>
      ))}
    </div>
  ) : (
  <div className="grid grid-cols-4 gap-4 sm:gap-8 border-t border-white/10 pt-6 mt-10 max-w-3xl"
    data-testid="hero-stats">
    <div>
      <p className={`font-heading text-[#00A8E8] ${compact ? 'text-2xl' : 'text-2xl sm:text-4xl'}`}><AnimatedCounter prefix="+" value={7000} /></p>
      <p className="text-white/60 text-xs sm:text-sm font-body mt-1">Serviços Realizados</p>
    </div>
    <div>
      <p className={`font-heading text-[#00A8E8] ${compact ? 'text-2xl' : 'text-2xl sm:text-4xl'}`}><AnimatedCounter isInfinity /></p>
      <p className="text-white/60 text-xs sm:text-sm font-body mt-1">Incontáveis Participantes</p>
    </div>
    <div>
      <p className={`font-heading text-[#00A8E8] ${compact ? 'text-2xl' : 'text-2xl sm:text-4xl'}`}><AnimatedCounter value={99.9} suffix="%" /></p>
      <p className="text-white/60 text-xs sm:text-sm font-body mt-1">Aprovação do Público</p>
    </div>
    <div>
      <p className={`font-heading text-[#00A8E8] ${compact ? 'text-2xl' : 'text-2xl sm:text-4xl'}`}><AnimatedCounter isInfinity /></p>
      <p className="text-white/60 text-xs sm:text-sm font-body mt-1">Clientes Satisfeitos</p>
    </div>
  </div>
  )
);

const HeroSection = () => {
  const [isMobile] = React.useState(() => window.matchMedia('(max-width: 768px)').matches);
  if (isMobile) return (
  <section id="inicio" className="relative bg-[#0A2463] overflow-hidden" data-testid="hero-section">
    <div className="px-4 pt-24">
      <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: 'easeOut' }}
        className="relative w-full aspect-video rounded-2xl overflow-hidden hero-glow">
        <video src={VIDEO_URL_MOBILE} className="w-full h-full object-cover" autoPlay muted loop playsInline data-testid="hero-bg-video"
          poster={POSTER} />
        <div className="absolute inset-0 bg-[#0A2463]/10 pointer-events-none" />
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-14 h-[5px] bg-[#00A8E8] rounded-t-full" />
      </motion.div>
    </div>
    <div className="relative px-6 pt-7 pb-10 text-center">
      <motion.h1 {...fadeUp(0.5)} className="font-heading text-[28px] text-white leading-tight uppercase tracking-wide">
        Sua ideia ganha vida.
      </motion.h1>
      <motion.p {...fadeUp(0.65)} className="text-white/70 text-sm font-body leading-relaxed max-w-[300px] mx-auto mt-3">
        Audiovisual e tecnologia integrada para eventos e instalações.
      </motion.p>
      <motion.div {...fadeUp(0.85)} className="mt-8">
        <HeroStats compact />
      </motion.div>
      <motion.div {...fadeUp(1.05)}>
        <a href="/orcamento" data-testid="hero-orcamento-btn"
          onClick={(e) => { e.preventDefault(); window.location.href = '/orcamento'; }}
          className="group bg-[#00A8E8] hover:bg-[#0090c9] text-white px-9 py-4 rounded-full font-bold inline-flex items-center justify-center gap-2 transition-colors text-sm cta-pulse whitespace-nowrap mt-9">
          <Send size={16} /> Solicite seu Orçamento
        </a>
      </motion.div>
      <motion.div className="flex justify-center mt-8" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
        <ChevronDown size={22} className="text-white/40" />
      </motion.div>
    </div>
  </section>
  );
  return (
  <section id="inicio" className="relative min-h-screen flex items-end bg-[#0A2463] overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0">
      <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: 'easeOut' }} className="w-full h-full">
        <video key="d" src={VIDEO_URL} className="w-full h-full object-cover" autoPlay muted loop playsInline data-testid="hero-bg-video"
          poster={POSTER} />
      </motion.div>
      <div className="absolute inset-0 bg-[#0A2463]/35" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0A2463]/95 via-[#0A2463]/50 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 to-transparent" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-16 w-full">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <motion.div {...fadeUp(0.3)} className="w-12 h-1 bg-[#00A8E8] rounded-full mb-4" />
          <motion.h1 {...fadeUp(0.45)} className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white leading-tight uppercase">
            Sua ideia ganha vida.
          </motion.h1>
          <motion.p {...fadeUp(0.6)} className="text-white/70 text-sm sm:text-base font-body mt-1">
            Audiovisual e tecnologia integrada para eventos e instalações.
          </motion.p>
        </div>
        <motion.div {...fadeUp(0.8)}>
          <a href="/orcamento" data-testid="hero-orcamento-btn"
            onClick={(e) => { e.preventDefault(); window.location.href = '/orcamento'; }}
            className="group bg-[#00A8E8] hover:bg-[#0090c9] text-white px-8 py-4 rounded-full font-bold inline-flex items-center justify-center gap-2 transition-colors text-sm sm:text-base cta-pulse whitespace-nowrap self-start sm:self-auto">
            <Send size={16} /> Solicite seu Orçamento
          </a>
        </motion.div>
      </div>
      <motion.div {...fadeUp(1.0)}>
        <HeroStats />
      </motion.div>
    </div>

    <motion.div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
      animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
      <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
        <div className="w-1 h-3 bg-white/60 rounded-full" />
      </div>
    </motion.div>
  </section>
  );
};

export default HeroSection;
